/**
 * EditableSelect — enum-aware tap-to-edit primitive (Phase 1.9.4 Item #4
 * full closure).
 *
 * Mirrors `EditableField` external API for enum/nullable-string columns
 * with a known vocabulary. Renders as a Chip in read mode (or a "+ Add"
 * affordance when value is null + canEdit); click → BottomSheet picker
 * with the option list.
 *
 * Used for: medicaid, service_delivery_model, gender_restrictions.
 *
 * Phase 1.9.4 design plan §1 (r2 locked):
 *   - canEdit: false → plain Chip (read-only display); no X-close, no
 *     dropdown affordance, no "+ Add" prompt. Matches the 6.F.4 role-
 *     gating pattern (PATCH-403 fallback UX is poor; pre-empt at the
 *     prop level).
 *   - value === null + canEdit → "+ Add {label}" pill-shaped button.
 *     Replaces the prior "X: not specified" placeholder Chip per the
 *     absorbed 1.9.5 Item #2 disposition (NULL/false → affordance, not
 *     placeholder string).
 *   - X close on a populated chip → save(null). Reverts to "not assessed"
 *     per the 0058 schema NULL semantic (locked at r2 Disposition #1).
 *
 * Phase 1.10 forward-compat: save handler abstracted via `onSave`. When
 * Phase 1.10 ships (Path A schema redesign), the OrganizationDetail
 * save handler rewires to write through `provider_highlight_assignments`
 * (or equivalent junction); the primitive itself is unchanged.
 */

import { useState, type ReactNode } from 'react';
import { BottomSheet } from './primitives/BottomSheet';
import { Chip, CHIP_VARIANT_CLASSES } from './primitives/Chip';
import type { ChipVariant } from './primitives/Chip';
import { SavingIndicator } from './SavingIndicator';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

// Chip variant tuple imported from primitives/Chip.jsx via JSDoc-style
// annotation. The file is .jsx; we type-narrow via this union.
// UX-13 S3 — the variant union re-exports from the Chip primitive (the
// local copy here was a second drifted mirror: it predated the semantic
// names, so 'affirm'/'caution'/'memory' didn't typecheck against it).
export type { ChipVariant } from './primitives/Chip';

export interface EditableSelectProps<T extends string> {
  /** Current saved value. NULL renders the empty-state affordance when canEdit. */
  value: T | null;
  /** Allowed values + display labels. Order preserved in picker. */
  options: ReadonlyArray<{ value: T; label: string }>;
  /** Save the new value. Resolves on success / rejects on failure. */
  onSave: (newValue: T | null, priorValue: T | null) => Promise<void>;
  /** ARIA label + "+ Add {label}" empty-state copy stem (e.g. "Medicaid"). */
  label: string;
  /**
   * Role gate. False → read-only display (plain Chip or muted "not
   * specified" Chip on null). True → tap-to-edit affordance.
   *
   * Defaults to false. Caller passes `role === 'owner' || role === 'data_steward'`.
   */
  canEdit?: boolean;
  /** Chip variant for the populated state (e.g. 'medicaid-yes'). */
  variant?: ChipVariant;
  /** Value-to-variant mapping override (e.g. Medicaid: Yes-Straight→medicaid-yes). */
  variantForValue?: (v: T) => ChipVariant;
  /** Override the "+ Add {label}" copy stem. */
  addLabel?: string;
  /** Optional value-to-display-label override; defaults to options[].label match. */
  formatValue?: (v: T) => string;
}

const SAVED_INDICATOR_MS = 1500;

export function EditableSelect<T extends string>(
  props: EditableSelectProps<T>,
): ReactNode {
  const {
    value,
    options,
    onSave,
    label,
    canEdit = false,
    variant = 'neutral',
    variantForValue,
    addLabel,
    formatValue,
  } = props;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveState, setSaveState] = useState<'saving' | 'saved' | 'error' | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const contact = useSupportContact();

  const currentVariant: ChipVariant =
    value !== null && variantForValue ? variantForValue(value) : variant;
  const displayLabel = value !== null
    ? (formatValue?.(value) ??
       options.find((o) => o.value === value)?.label ??
       value)
    : null;

  async function commit(newValue: T | null): Promise<void> {
    if (newValue === value) {
      setPickerOpen(false);
      return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      await onSave(newValue, value);
      setSaveState('saved');
      setPickerOpen(false);
      setTimeout(() => setSaveState(null), SAVED_INDICATOR_MS);
    } catch (e) {
      setSaveState('error');
      setSaveError(pageWriteText(e, contact));
    }
  }

  // Read-only display path (canEdit=false).
  if (!canEdit) {
    if (value === null) {
      return (
        <Chip variant="neutral">{label}: not specified</Chip>
      );
    }
    return (
      <Chip variant={currentVariant}>{displayLabel}</Chip>
    );
  }

  // Editable path.
  const emptyAffordanceLabel = addLabel ?? `+ Add ${label}`;

  return (
    <span className="inline-flex items-center gap-1">
      {value === null ? (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          aria-label={`Add ${label}`}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-sage-300 px-2.5 py-1 text-[11px] font-medium text-sage-600 hover:bg-sage-50 hover:border-sage-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 transition-colors"
        >
          {emptyAffordanceLabel}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            aria-label={`Edit ${label} (currently ${displayLabel})`}
            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium cursor-pointer hover:opacity-80 transition-opacity ${chipBackgroundForVariant(currentVariant)}`}
          >
            {displayLabel}
          </button>
          <button
            type="button"
            onClick={() => void commit(null)}
            aria-label={`Clear ${label}`}
            className="text-ink-soft hover:text-ink cursor-pointer text-xs leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded-sm"
          >
            ✕
          </button>
        </span>
      )}
      <SavingIndicator state={saveState} errorMessage={saveError ?? undefined} />

      <BottomSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={label}
      >
        <div role="listbox" className="px-5 py-3">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => void commit(opt.value)}
                className={`flex w-full items-center gap-3 py-2.5 px-1 border-b border-sage-800/[8%] last:border-0 cursor-pointer text-left ${isSelected ? 'text-sage-700 font-semibold' : 'text-ink'}`}
              >
                <span
                  className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center shrink-0 text-[10px] ${isSelected ? 'border-sage-400 bg-sage-800 text-cream' : 'border-sage-300'}`}
                >
                  {isSelected && '✓'}
                </span>
                <span className="text-sm">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </span>
  );
}

/** UX-13 S3 — the chip surface derives from the Chip primitive's exported
 *  variant map (CHIP_VARIANT_CLASSES). The previous hand-copied switch here
 *  drifted to the retired Tailwind hues (green/amber/blue/cyan/rose) when the
 *  primitive moved to the brand identity set — edit-mode chips rendered the
 *  old palette beside read-only brand chips. One map, no mirrors. */
function chipBackgroundForVariant(variant: ChipVariant): string {
  return CHIP_VARIANT_CLASSES[variant] ?? CHIP_VARIANT_CLASSES.neutral;
}
